import { useIntl } from "react-intl";
import React, { useState } from "react";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";

import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useAlert } from "@/src/common/Alert";

import { useDeleteEmployeeRoleAlert } from "./useDeleteEmployeeRoleAlert";
import { EmployeeRoleEditComponent } from "./EmployeeRoleEditComponent";

import { EmployeeRoleEditQuery } from "./__generated__/EmployeeRoleEditQuery.graphql";
import { EmployeeRoleEditForm_employeeRole$key } from "./__generated__/EmployeeRoleEditForm_employeeRole.graphql";
import { EmployeeRoleEditMainUpdateMutation } from "./__generated__/EmployeeRoleEditMainUpdateMutation.graphql";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorParamList,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";

type ParamList = {
  EmployeeRoleEditMain: UserLoggedInNavigatorParamList["EmployeeRoleEdit"];
};

type EmployeeRoleEditScreenProps<T extends keyof ParamList> = {
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"EmployeeRoleEdit">,
    NativeStackNavigationProp<ParamList, T>
  >;
  route: RouteProp<ParamList, T>;
};

const Stack = createNativeStackNavigator<ParamList>();

export const EmployeeRoleEdit = withWebModal(EmployeeRoleEditC);

function EmployeeRoleEditC(
  props: UserLoggedInNavigatorScreenProps<"EmployeeRoleEdit">
) {
  const intl = useIntl();
  const getOpts = useNavigationHeaderOptions();
  const opts = getOpts({
    type: "CLOSE",
    title: intl.formatMessage({
      defaultMessage: "Role",
      description: "EmployeeRoleEdit: Header title",
    }),
    onClose: () => {
      props.navigation.goBack();
    },
  });

  return (
    <Stack.Navigator initialRouteName="EmployeeRoleEditMain">
      <Stack.Screen
        name="EmployeeRoleEditMain"
        component={EmployeeRoleEditMain}
        initialParams={props.route.params}
        options={{
          ...opts,
        }}
      />
    </Stack.Navigator>
  );
}

function EmployeeRoleEditMain(
  props: EmployeeRoleEditScreenProps<"EmployeeRoleEditMain">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerEmployeeRoleEdit {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerEmployeeRoleEdit({
  route: { params },
  navigation,
  fetchKey,
}: EmployeeRoleEditScreenProps<"EmployeeRoleEditMain"> & { fetchKey: string }) {
  const data = useLazyLoadQuery<EmployeeRoleEditQuery>(
    graphql`
      query EmployeeRoleEditQuery($id: ID!) {
        employeeRole(id: $id) {
          ...EmployeeRoleEditForm_employeeRole
        }
      }
    `,
    {
      id: params.id,
    },
    {
      fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  if (!data.employeeRole) {
    return null;
  }

  return (
    <EmployeeRoleEditForm
      employeeRole={data.employeeRole}
      roleId={params.id}
      navigation={navigation}
    />
  );
}

function EmployeeRoleEditForm({
  employeeRole: employeeRoleKey,
  roleId,
  navigation,
}: {
  employeeRole: EmployeeRoleEditForm_employeeRole$key;
  roleId: string;
  navigation: EmployeeRoleEditScreenProps<"EmployeeRoleEditMain">["navigation"];
}) {
  const employeeRole = useFragment(
    graphql`
      fragment EmployeeRoleEditForm_employeeRole on EmployeeRole {
        id
        name
        team {
          id
        }
        employees {
          id
        }
      }
    `,
    employeeRoleKey
  );

  const intl = useIntl();
  const Alert = useAlert();

  const [name, setName] = useState(employeeRole.name);
  const [employeeIds, setEmployeeIds] = useState<string[]>(
    employeeRole.employees.map((e) => e.id)
  );

  const [updateRole, updateLoading] =
    useMutation<EmployeeRoleEditMainUpdateMutation>(graphql`
      mutation EmployeeRoleEditMainUpdateMutation(
        $id: ID!
        $input: UpdateEmployeeRoleInput!
      ) {
        store {
          employeeRole(id: $id) {
            update(input: $input) {
              role {
                id
                name
                employees {
                  id
                }
              }
            }
          }
        }
      }
    `);

  const onUpdate = () => {
    updateRole({
      variables: {
        id: roleId,
        input: {
          name,
          employeeIds,
        },
      },
      onCompleted: navigation.goBack,
      onError: () => {
        Alert.alert(
          intl.formatMessage({
            defaultMessage: "Failed to update role",
            description: "EmployeeRoleEdit: Error title",
          })
        );
      },
    });
  };

  const onPressDelete = useDeleteEmployeeRoleAlert({
    id: roleId,
    onCompleted: navigation.goBack,
    name: employeeRole.name,
  });

  const hasChanges =
    name !== employeeRole.name ||
    JSON.stringify(employeeIds.slice().sort()) !==
      JSON.stringify(
        employeeRole.employees
          .map((e) => e.id)
          .slice()
          .sort()
      );

  useNavigationHeader({
    type: "SAVE",
    onCancel: navigation.goBack,
    onSave: onUpdate,
    saveLoading: updateLoading,
    disabled: name.length < 1 || !hasChanges,
    title: intl.formatMessage({
      defaultMessage: "Role",
      description: "EmployeeRoleEdit: Header title",
    }),
    defaultContentStyle: true,
    more: [
      {
        label: intl.formatMessage({
          defaultMessage: "Delete role",
          description: "EmployeeRoleEdit delete role button title",
        }),
        onPress: onPressDelete,
        icon: "trash",
        color: "negative",
        type: "button",
      },
    ],
  });

  return (
    <EmployeeRoleEditComponent
      employeeIds={employeeIds}
      name={name}
      setName={setName}
      setEmployeeIds={setEmployeeIds}
      teamId={employeeRole.team.id}
    />
  );
}
